import { Assets, Container, Sprite, type Texture } from 'pixi.js';
import {
  ambientDrift,
  approach,
  layerOffset,
  normalisePointer,
  spriteLayout,
  tiltToPointer,
  type SpriteLayout,
  type Vec2,
} from './parallaxMath.js';

/**
 * iOS 13+ gates the motion sensors behind a permission prompt that must be
 * triggered from a user gesture; the static method that requests it is absent
 * on Android and desktop, which need no permission. Feature-detected, never
 * assumed to exist.
 */
type OrientationPermission = 'granted' | 'denied' | 'default';
type OrientationEventCtor = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<OrientationPermission>;
};

/** Live render-surface size (the scene's `Viewport`), injected so the parallax
 * fits the canvas box — the top half on phones — rather than the whole window. */
interface ViewportSize {
  readonly width: number;
  readonly height: number;
}

/**
 * Mouse-driven parallax foreground (game-design §8, content-schema §2 `background`).
 *
 * The age's full-bleed backdrop is its own `scene` image, rendered separately as the
 * scene's plain backdrop sprite; this class only composes the transparent sprite
 * layers stacked above it at increasing z depths. The math lives in ./parallaxMath.ts
 * (pure, unit-tested); this class is the thin Pixi/DOM shell that loads whatever art
 * exists and composes it. Loading is fire-and-forget per layer — dev builds usually
 * lack `/content/` art, and every missing texture simply leaves that layer out.
 */

/** One sprite layer resolved by the app: an art URL at a parallax depth. */
export interface ParallaxLayerSpec {
  readonly url: string;
  /** Higher = nearer the camera, larger offset. */
  readonly z: number;
}

export interface ParallaxOptions {
  readonly sprites: readonly ParallaxLayerSpec[];
}

interface LiveLayer {
  readonly sprite: Sprite;
  readonly z: number;
  readonly index: number;
  readonly layout: SpriteLayout;
  offsetX: number;
  offsetY: number;
}

export class ParallaxBackdrop {
  readonly container = new Container();

  private readonly viewport: () => ViewportSize;
  private readonly layers: LiveLayer[] = [];
  private pointer: Vec2 = { x: 0, y: 0 };
  /** Latest gyro-derived camera position, or null until the first tilt reading arrives. */
  private tilt: Vec2 | null = null;
  /** Front-back angle the phone rested at when tracking began (the neutral centre). */
  private referenceBeta: number | null = null;
  private orientationBound = false;
  private phase = 0;
  private destroyed = false;

  private readonly onPointerMove = (event: PointerEvent): void => {
    const { width, height } = this.viewport();
    this.pointer = normalisePointer(event.clientX, event.clientY, width, height);
  };

  private readonly onDeviceOrientation = (event: DeviceOrientationEvent): void => {
    const { beta, gamma } = event;
    if (beta === null || gamma === null) {
      return;
    }
    this.referenceBeta ??= beta;
    this.tilt = tiltToPointer(beta, gamma, this.referenceBeta);
  };

  /** iOS permission is requested on the first tap; bound only until it fires. */
  private readonly onFirstGesture = (): void => {
    window.removeEventListener('pointerdown', this.onFirstGesture);
    const ctor = window.DeviceOrientationEvent as OrientationEventCtor | undefined;
    const request = ctor?.requestPermission;
    if (request === undefined) {
      return;
    }
    void request
      .call(ctor)
      .then((state) => {
        if (state === 'granted') {
          this.bindDeviceOrientation();
        }
      })
      .catch(() => {
        // Permission unavailable or denied — the mouse/keyboard path still works.
      });
  };

  private readonly onResize = (): void => {
    this.fit();
  };

  constructor(options: ParallaxOptions, viewport: () => ViewportSize) {
    this.viewport = viewport;
    this.container.sortableChildren = true;

    options.sprites.forEach((spec, index) => {
      void Assets.load<Texture>(spec.url)
        .then((texture) => {
          if (this.destroyed) {
            return;
          }
          const sprite = new Sprite(texture);
          sprite.anchor.set(0.5);
          sprite.zIndex = spec.z;
          const layer: LiveLayer = {
            sprite,
            z: spec.z,
            index,
            layout: spriteLayout(index, spec.z),
            offsetX: 0,
            offsetY: 0,
          };
          this.layers.push(layer);
          this.container.addChild(sprite);
          this.fitLayer(layer);
        })
        .catch(() => {
          // This layer's render doesn't exist yet — the rest still compose.
        });
    });

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('resize', this.onResize);
    this.setupDeviceOrientation();
  }

  /**
   * Wire up gyroscope parallax for phones. On iOS 13+ the sensors need a
   * permission granted from a user gesture, so we defer to the first tap;
   * elsewhere (Android, older iOS) the readings flow immediately. Where no
   * gyroscope exists the listener simply never fires and the mouse path stands.
   */
  private setupDeviceOrientation(): void {
    const ctor = window.DeviceOrientationEvent as OrientationEventCtor | undefined;
    if (ctor === undefined) {
      return;
    }
    if (typeof ctor.requestPermission === 'function') {
      window.addEventListener('pointerdown', this.onFirstGesture);
    } else {
      this.bindDeviceOrientation();
    }
  }

  private bindDeviceOrientation(): void {
    if (this.orientationBound) {
      return;
    }
    this.orientationBound = true;
    window.addEventListener('deviceorientation', this.onDeviceOrientation);
  }

  private fitLayer(layer: LiveLayer): void {
    const { width, height } = layer.sprite.texture;
    if (width <= 0 || height <= 0) {
      return;
    }
    const view = this.viewport();
    const scale = (view.height * layer.layout.heightFrac) / height;
    layer.sprite.scale.set(scale);
    layer.sprite.x = layer.layout.fx * view.width;
    layer.sprite.y = layer.layout.fy * view.height;
  }

  private fit(): void {
    for (const layer of this.layers) {
      this.fitLayer(layer);
    }
  }

  /** Advance easing + drift; called once per render frame by the owning scene. */
  animate(dtSeconds: number): void {
    if (dtSeconds <= 0) {
      return;
    }
    this.phase += dtSeconds;
    // Accessibility: with reduced motion on, the camera eases home and holds still.
    const still = document.body.classList.contains('reduced-motion');
    // The gyro drives the same camera as the mouse; once a phone reports tilt it
    // takes over (a touch device has no hovering pointer to fall back on).
    const source = this.tilt ?? this.pointer;
    const pointer = still ? { x: 0, y: 0 } : source;

    const view = this.viewport();
    for (const layer of this.layers) {
      const target = layerOffset(pointer, layer.z);
      layer.offsetX = approach(layer.offsetX, target.x, dtSeconds);
      layer.offsetY = approach(layer.offsetY, target.y, dtSeconds);
      const drift = still ? { x: 0, y: 0 } : ambientDrift(this.phase, layer.index);
      layer.sprite.x = layer.layout.fx * view.width + layer.offsetX + drift.x;
      layer.sprite.y = layer.layout.fy * view.height + layer.offsetY + drift.y;
    }
  }

  destroy(): void {
    this.destroyed = true;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pointerdown', this.onFirstGesture);
    window.removeEventListener('deviceorientation', this.onDeviceOrientation);
    this.layers.length = 0;
    this.container.destroy({ children: true });
  }
}
